import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { catchError, map, of } from "rxjs";
import { isBillingDelegated } from "src/app/pages/billing/billing.model";
import { BillingService } from "src/app/pages/billing/billing.service";
import { isUserOnboardingInProgress } from "src/app/pages/profile/profile.model";
import { ProfileService } from "src/app/pages/profile/profile.service";
import { StoreService } from "src/app/services/apps/store/store.service";

/**
 * For users with completed onboarding, requires active billing
 * (customer + payment method + active/trialing sub).
 *
 * While onboarding is incomplete, this guard does not intervene; billing is
 * handled by the initial-setup flow.
 * Delegated users (billingMode: "delegated") always pass — their billing is
 * covered by the store owner and they never need a Stripe subscription.
 * Users with no owned stores always pass — they have no billing obligation yet
 * and need to be able to link a store or accept a shared-store invitation.
 *
 * When no billing customer exists, owned stores are resolved via StoreService
 * (cached after first load; one HTTP call if not yet loaded).
 */
export const userBillingGuard: CanActivateFn = (_route, state) => {
  const billingService = inject(BillingService);
  const profileService = inject(ProfileService);
  const storeService = inject(StoreService);
  const router = inject(Router);

  if (isUserOnboardingInProgress(profileService.userProfile())) {
    return true;
  }

  const customer = billingService.getPreloadedBilling()?.customer;

  if (isBillingDelegated(customer)) {
    return true;
  }

  if (billingService.hasActiveBillingCached()) {
    return true;
  }

  const billingUrl = (returnUrl: string) => {
    const tree = router.parseUrl("/activate-service/billing");
    tree.queryParams["returnUrl"] = returnUrl;
    return tree;
  };

  // Billing not active. If the customer record already has ownedStoreIds, use it directly.
  if (customer) {
    const hasOwnedStores = (customer.ownedStoreIds?.length ?? 0) > 0;
    return hasOwnedStores ? billingUrl(state.url) : true;
  }

  // No customer yet — resolve owned stores from StoreService cache (or API if not loaded).
  // A store without isBillingDelegated is one the user owns.
  return storeService.getCachedOrLoad().pipe(
    map((stores) => {
      const hasOwnedStores = stores.some((s) => !s.isBillingDelegated);
      return hasOwnedStores ? billingUrl(state.url) : true;
    }),
    catchError(() => of(billingUrl(state.url))),
  );
};
